/**
 * ── `scans.geo_country` → ISO-3166-1 alpha-3, the choropleth's JOIN KEY (VERA-260) ──
 *
 * The per-country choropleth paints polygons from
 * `packages/ui/components/display/Map/countries/world-countries.geo.json`, whose features
 * are keyed on ISO-3 (`properties.a3`). Nothing in `scans` is keyed that way, so a
 * translation has to happen somewhere. This is it, and it runs SERVER-SIDE — the
 * aggregate endpoint resolves each raw value and returns rows already keyed on ISO-3,
 * plus an explicit list of what did not resolve.
 *
 * ## ⚠️ `scans.geo_country` IS NOT ONE FORMAT. It is at least two, and they differ BY
 * ENVIRONMENT — which is why a "just uppercase it" join would pass in dev and fail in prod
 *
 * Two writers populate the column and they disagree:
 *
 * | writer | shape | seen where |
 * |---|---|---|
 * | `public-verify-log.drizzle.repository.ts` — `fingerprint.geoip.country`, which is `geoip-lite` via `express-fingerprint` | **ISO-2 code** (`"US"`, `"FR"`) | production |
 * | `seed-scan-volume.ts` — `SEED_PLACES[].country` | **full English name** (`"United States"`, `"South Korea"`) | dev / seeded |
 * | `log-public-access.use-case.ts` — a client-supplied `country` off the public verify form | whatever the caller sent | anywhere |
 *
 * A local database today holds `"United States"`, `"Hong Kong"`, `"South Korea"`. A
 * production one will hold `"US"`, `"HK"`, `"KR"`. So the resolver accepts BOTH, and the
 * verification you do locally is not evidence about production — which is exactly why the
 * unresolved values are counted and returned rather than being allowed to fall on the
 * floor.
 *
 * ## The failure this exists to make impossible
 *
 * A country that does not resolve paints NOTHING. On a choropleth "no polygon" is
 * visually identical to "no scans there" — so a silent join miss does not degrade the
 * map, it INVERTS it: a brand's third-biggest market renders as a market it has never
 * reached. There is no way for a reader to tell. Hence `resolveCountryCode` returning
 * `null` rather than a guess, and hence the endpoint reporting the misses by name and
 * count (mirroring `geographic_reach_unattributed`, which already reports located scans
 * that carry no country at all).
 *
 * ## Name variants are the real work
 *
 * "United States" / "United States of America" / "USA"; "South Korea" / "Korea, Republic
 * of" / "Republic of Korea"; "Russia" / "Russian Federation"; "Czechia" / "Czech
 * Republic"; "Myanmar" / "Burma". Natural Earth's own labels are abbreviated in a third
 * way again ("Bosnia and Herz.", "Dem. Rep. Congo", "St. Vin. and Gren."), and those are
 * the strings in `COUNTRY_TABLE` — so the long forms a human or an import would actually
 * write are aliases, not the canonical entries. `country-join.guard.spec.ts` fails on any
 * alias that points at an unknown code and on any table row with no polygon.
 *
 * ## ⚠️ NEVER "fix" a miss by making the matcher fuzzier
 *
 * Prefix or edit-distance matching would resolve `"Niger"` to Nigeria, `"Guinea"` to
 * Equatorial Guinea or Guinea-Bissau, and `"Samoa"` to American Samoa — quietly, and with
 * the same confidence as a correct match. A wrong country on a provenance product is
 * worse than a missing one, because a missing one is reported. Add an explicit alias
 * instead; that is what `COUNTRY_ALIASES` is for.
 */

/** One country the map can paint. `a2` is null for the three disputed territories that
 *  carry no ISO-2 at all (N. Cyprus, Somaliland, Siachen Glacier) — they exist so the
 *  world has no holes, and no geoip database will ever return them. */
export interface CountryRecord {
  /** ISO-3166-1 alpha-3. The join key, and the id of a GeoJSON feature. */
  a3: string;
  /** ISO-3166-1 alpha-2, as `geoip-lite` emits. */
  a2: string | null;
  /** Natural Earth's own short label — what the map renders, so the tooltip and the
   *  polygon agree on the country's name. */
  name: string;
}

/**
 * The 240 features in `world-countries.geo.json`, one row each, `a3|a2|name`.
 *
 * Encoded as pipe-delimited strings rather than 240 object literals purely for
 * readability at this length — the parsed shape is `CountryRecord`. GENERATED from the
 * asset (see `context/scripts/build-country-geojson.mjs`); the guard asserts this set and
 * the asset's feature set are IDENTICAL in both directions, so neither can gain or lose a
 * country without the other.
 */
const COUNTRY_ROWS: readonly string[] = [
  'ABW|AW|Aruba', 'AFG|AF|Afghanistan', 'AGO|AO|Angola', 'AIA|AI|Anguilla',
  'ALA|AX|Åland', 'ALB|AL|Albania', 'AND|AD|Andorra', 'ARE|AE|United Arab Emirates',
  'ARG|AR|Argentina', 'ARM|AM|Armenia', 'ASM|AS|American Samoa', 'ATA|AQ|Antarctica',
  'ATF|TF|Fr. S. Antarctic Lands', 'ATG|AG|Antigua and Barb.', 'AUS|AU|Australia',
  'AUT|AT|Austria', 'AZE|AZ|Azerbaijan', 'BDI|BI|Burundi', 'BEL|BE|Belgium',
  'BEN|BJ|Benin', 'BFA|BF|Burkina Faso', 'BGD|BD|Bangladesh', 'BGR|BG|Bulgaria',
  'BHR|BH|Bahrain', 'BHS|BS|Bahamas', 'BIH|BA|Bosnia and Herz.', 'BLM|BL|St-Barthélemy',
  'BLR|BY|Belarus', 'BLZ|BZ|Belize', 'BMU|BM|Bermuda', 'BOL|BO|Bolivia', 'BRA|BR|Brazil',
  'BRB|BB|Barbados', 'BRN|BN|Brunei', 'BTN|BT|Bhutan', 'BWA|BW|Botswana',
  'CAF|CF|Central African Rep.', 'CAN|CA|Canada', 'CHE|CH|Switzerland', 'CHL|CL|Chile',
  'CHN|CN|China', "CIV|CI|Côte d'Ivoire", 'CMR|CM|Cameroon', 'COD|CD|Dem. Rep. Congo',
  'COG|CG|Congo', 'COK|CK|Cook Is.', 'COL|CO|Colombia', 'COM|KM|Comoros',
  'CPV|CV|Cabo Verde', 'CRI|CR|Costa Rica', 'CUB|CU|Cuba', 'CUW|CW|Curaçao',
  'CYM|KY|Cayman Is.', 'CYN||N. Cyprus', 'CYP|CY|Cyprus', 'CZE|CZ|Czechia',
  'DEU|DE|Germany', 'DJI|DJ|Djibouti', 'DMA|DM|Dominica', 'DNK|DK|Denmark',
  'DOM|DO|Dominican Rep.', 'DZA|DZ|Algeria', 'ECU|EC|Ecuador', 'EGY|EG|Egypt',
  'ERI|ER|Eritrea', 'ESH|EH|W. Sahara', 'ESP|ES|Spain', 'EST|EE|Estonia',
  'ETH|ET|Ethiopia', 'FIN|FI|Finland', 'FJI|FJ|Fiji', 'FLK|FK|Falkland Is.',
  'FRA|FR|France', 'FRO|FO|Faeroe Is.', 'FSM|FM|Micronesia', 'GAB|GA|Gabon',
  'GBR|GB|United Kingdom', 'GEO|GE|Georgia', 'GGY|GG|Guernsey', 'GHA|GH|Ghana',
  'GIN|GN|Guinea', 'GMB|GM|Gambia', 'GNB|GW|Guinea-Bissau', 'GNQ|GQ|Eq. Guinea',
  'GRC|GR|Greece', 'GRD|GD|Grenada', 'GRL|GL|Greenland', 'GTM|GT|Guatemala',
  'GUM|GU|Guam', 'GUY|GY|Guyana', 'HKG|HK|Hong Kong', 'HMD|HM|Heard I. and McDonald Is.',
  'HND|HN|Honduras', 'HRV|HR|Croatia', 'HTI|HT|Haiti', 'HUN|HU|Hungary',
  'IDN|ID|Indonesia', 'IMN|IM|Isle of Man', 'IND|IN|India', 'IOT|IO|Br. Indian Ocean Ter.',
  'IRL|IE|Ireland', 'IRN|IR|Iran', 'IRQ|IQ|Iraq', 'ISL|IS|Iceland', 'ISR|IL|Israel',
  'ITA|IT|Italy', 'JAM|JM|Jamaica', 'JEY|JE|Jersey', 'JOR|JO|Jordan', 'JPN|JP|Japan',
  'KAS||Siachen Glacier', 'KAZ|KZ|Kazakhstan', 'KEN|KE|Kenya', 'KGZ|KG|Kyrgyzstan',
  'KHM|KH|Cambodia', 'KIR|KI|Kiribati', 'KNA|KN|St. Kitts and Nevis', 'KOR|KR|South Korea',
  'KOS|XK|Kosovo', 'KWT|KW|Kuwait', 'LAO|LA|Laos', 'LBN|LB|Lebanon', 'LBR|LR|Liberia',
  'LBY|LY|Libya', 'LCA|LC|Saint Lucia', 'LIE|LI|Liechtenstein', 'LKA|LK|Sri Lanka',
  'LSO|LS|Lesotho', 'LTU|LT|Lithuania', 'LUX|LU|Luxembourg', 'LVA|LV|Latvia',
  'MAC|MO|Macao', 'MAF|MF|St-Martin', 'MAR|MA|Morocco', 'MCO|MC|Monaco', 'MDA|MD|Moldova',
  'MDG|MG|Madagascar', 'MDV|MV|Maldives', 'MEX|MX|Mexico', 'MHL|MH|Marshall Is.',
  'MKD|MK|North Macedonia', 'MLI|ML|Mali', 'MLT|MT|Malta', 'MMR|MM|Myanmar',
  'MNE|ME|Montenegro', 'MNG|MN|Mongolia', 'MNP|MP|N. Mariana Is.', 'MOZ|MZ|Mozambique',
  'MRT|MR|Mauritania', 'MSR|MS|Montserrat', 'MUS|MU|Mauritius', 'MWI|MW|Malawi',
  'MYS|MY|Malaysia', 'NAM|NA|Namibia', 'NCL|NC|New Caledonia', 'NER|NE|Niger',
  'NFK|NF|Norfolk Island', 'NGA|NG|Nigeria', 'NIC|NI|Nicaragua', 'NIU|NU|Niue',
  'NLD|NL|Netherlands', 'NOR|NO|Norway', 'NPL|NP|Nepal', 'NRU|NR|Nauru',
  'NZL|NZ|New Zealand', 'OMN|OM|Oman', 'PAK|PK|Pakistan', 'PAN|PA|Panama',
  'PCN|PN|Pitcairn Is.', 'PER|PE|Peru', 'PHL|PH|Philippines', 'PLW|PW|Palau',
  'PNG|PG|Papua New Guinea', 'POL|PL|Poland', 'PRI|PR|Puerto Rico', 'PRK|KP|North Korea',
  'PRT|PT|Portugal', 'PRY|PY|Paraguay', 'PSE|PS|Palestine', 'PYF|PF|Fr. Polynesia',
  'QAT|QA|Qatar', 'ROU|RO|Romania', 'RUS|RU|Russia', 'RWA|RW|Rwanda',
  'SAU|SA|Saudi Arabia', 'SDN|SD|Sudan', 'SEN|SN|Senegal', 'SGP|SG|Singapore',
  'SGS|GS|S. Geo. and the Is.', 'SHN|SH|Saint Helena', 'SLB|SB|Solomon Is.',
  'SLE|SL|Sierra Leone', 'SLV|SV|El Salvador', 'SMR|SM|San Marino', 'SOL||Somaliland',
  'SOM|SO|Somalia', 'SPM|PM|St. Pierre and Miquelon', 'SRB|RS|Serbia', 'SSD|SS|S. Sudan',
  'STP|ST|São Tomé and Principe', 'SUR|SR|Suriname', 'SVK|SK|Slovakia', 'SVN|SI|Slovenia',
  'SWE|SE|Sweden', 'SWZ|SZ|eSwatini', 'SXM|SX|Sint Maarten', 'SYC|SC|Seychelles',
  'SYR|SY|Syria', 'TCA|TC|Turks and Caicos Is.', 'TCD|TD|Chad', 'TGO|TG|Togo',
  'THA|TH|Thailand', 'TJK|TJ|Tajikistan', 'TKM|TM|Turkmenistan', 'TLS|TL|Timor-Leste',
  'TON|TO|Tonga', 'TTO|TT|Trinidad and Tobago', 'TUN|TN|Tunisia', 'TUR|TR|Turkey',
  'TUV|TV|Tuvalu', 'TWN|TW|Taiwan', 'TZA|TZ|Tanzania', 'UGA|UG|Uganda', 'UKR|UA|Ukraine',
  'URY|UY|Uruguay', 'USA|US|United States of America', 'UZB|UZ|Uzbekistan',
  'VAT|VA|Vatican', 'VCT|VC|St. Vin. and Gren.', 'VEN|VE|Venezuela',
  'VGB|VG|British Virgin Is.', 'VIR|VI|U.S. Virgin Is.', 'VNM|VN|Vietnam',
  'VUT|VU|Vanuatu', 'WLF|WF|Wallis and Futuna Is.', 'WSM|WS|Samoa', 'YEM|YE|Yemen',
  'ZAF|ZA|South Africa', 'ZMB|ZM|Zambia', 'ZWE|ZW|Zimbabwe',
];

export const COUNTRY_TABLE: readonly CountryRecord[] = COUNTRY_ROWS.map((row) => {
  const [a3, a2, name] = row.split('|');
  return {a3, a2: a2 || null, name};
});

/** ISO-3 → the label the map and the tooltip both use. */
export const COUNTRY_NAME_BY_A3: Readonly<Record<string, string>> =
  COUNTRY_TABLE.reduce<Record<string, string>>((acc, c) => {
    acc[c.a3] = c.name;
    return acc;
  }, {});

/**
 * Everything a writer might plausibly put in `geo_country` that is NOT already the ISO-2
 * code, the ISO-3 code, or Natural Earth's own label — mapped explicitly.
 *
 * Three groups, and the middle one is the one people forget:
 *
 * 1. **Real-world variants** — official long forms (`"Russian Federation"`), former names
 *    (`"Burma"`, `"Swaziland"`, `"Zaire"`), colloquialisms (`"Holland"`, `"UK"`), and the
 *    ISO short-name inversions (`"Korea, Republic of"`) that appear in almost every
 *    country dropdown ever shipped.
 * 2. **Expansions of Natural Earth's OWN abbreviations.** The canonical `name` above is
 *    `"Bosnia and Herz."`, so the string a human would actually type — `"Bosnia and
 *    Herzegovina"` — needs an alias, and there are ~30 of these. Missing them was the
 *    likeliest source of a silent miss, because the table looks complete without them.
 * 3. **Codes that are not in the table** — `XKX` (Kosovo's de-facto ISO-3, where the
 *    asset carries Natural Earth's `KOS`), and `UK` (a reserved ISO-2 that is not `GB`
 *    but is written constantly).
 *
 * Keys are matched after `normalize()`, so case, accents, punctuation and spacing are
 * already handled — `"cote d'ivoire"`, `"Côte d’Ivoire"` and `"COTE DIVOIRE"` all reduce
 * to the same key and need only one entry here.
 *
 * ⚠️ `"England"`, `"Scotland"`, `"Wales"` and `"Northern Ireland"` map to `GBR`. They are
 * not countries in ISO-3166 and no geoip source emits them, but a hand-entered address
 * field will, and mapping them is strictly better than reporting a miss for a value whose
 * intent is unambiguous. `"Great Britain"` and `"Britain"` are handled the same way even
 * though they are not co-extensive with the UK — the polygon is the same either way.
 */
export const COUNTRY_ALIASES: Readonly<Record<string, string>> = {
  // ── 1. Real-world variants ────────────────────────────────────────────────────────
  'united states': 'USA',
  'united states of america': 'USA',
  'usa': 'USA',
  'us': 'USA',
  'america': 'USA',
  'uk': 'GBR',
  'great britain': 'GBR',
  'britain': 'GBR',
  'england': 'GBR',
  'scotland': 'GBR',
  'wales': 'GBR',
  'northern ireland': 'GBR',
  'united kingdom of great britain and northern ireland': 'GBR',
  'russian federation': 'RUS',
  'czech republic': 'CZE',
  'south korea': 'KOR',
  'korea republic of': 'KOR',
  'republic of korea': 'KOR',
  'korea south': 'KOR',
  'north korea': 'PRK',
  'korea democratic peoples republic of': 'PRK',
  'democratic peoples republic of korea': 'PRK',
  'korea north': 'PRK',
  'burma': 'MMR',
  'myanmar burma': 'MMR',
  'cape verde': 'CPV',
  'swaziland': 'SWZ',
  'eswatini': 'SWZ',
  'macedonia': 'MKD',
  'the former yugoslav republic of macedonia': 'MKD',
  'fyrom': 'MKD',
  'holland': 'NLD',
  'the netherlands': 'NLD',
  'netherlands the': 'NLD',
  'vatican city': 'VAT',
  'holy see': 'VAT',
  'vatican city state': 'VAT',
  'uae': 'ARE',
  'emirates': 'ARE',
  'ivory coast': 'CIV',
  'cote divoire': 'CIV',
  'democratic republic of the congo': 'COD',
  'democratic republic of congo': 'COD',
  'congo kinshasa': 'COD',
  'congo the democratic republic of the': 'COD',
  'drc': 'COD',
  'zaire': 'COD',
  'republic of the congo': 'COG',
  'congo brazzaville': 'COG',
  'congo republic': 'COG',
  'lao peoples democratic republic': 'LAO',
  'laos pdr': 'LAO',
  'viet nam': 'VNM',
  'syrian arab republic': 'SYR',
  'islamic republic of iran': 'IRN',
  'iran islamic republic of': 'IRN',
  'united republic of tanzania': 'TZA',
  'tanzania united republic of': 'TZA',
  'plurinational state of bolivia': 'BOL',
  'bolivia plurinational state of': 'BOL',
  'bolivarian republic of venezuela': 'VEN',
  'venezuela bolivarian republic of': 'VEN',
  'republic of moldova': 'MDA',
  'moldova republic of': 'MDA',
  'brunei darussalam': 'BRN',
  'state of palestine': 'PSE',
  'palestinian territory': 'PSE',
  'palestinian territory occupied': 'PSE',
  'palestine state of': 'PSE',
  'taiwan province of china': 'TWN',
  'chinese taipei': 'TWN',
  'republic of china': 'TWN',
  'hong kong sar': 'HKG',
  'hong kong sar china': 'HKG',
  'hong kong china': 'HKG',
  'hong kong special administrative region of china': 'HKG',
  'macau': 'MAC',
  'macao sar': 'MAC',
  'macau sar china': 'MAC',
  'macao special administrative region of china': 'MAC',
  'turkiye': 'TUR',
  'east timor': 'TLS',
  'timor leste': 'TLS',
  'cabo verde': 'CPV',
  'peoples republic of china': 'CHN',
  'mainland china': 'CHN',
  'republic of ireland': 'IRL',
  'south sudan': 'SSD',
  'western sahara': 'ESH',
  'saint martin french part': 'MAF',
  'sint maarten dutch part': 'SXM',
  'saint helena ascension and tristan da cunha': 'SHN',
  'reunion': 'FRA',
  'martinique': 'FRA',
  'guadeloupe': 'FRA',
  'french guiana': 'FRA',
  'mayotte': 'FRA',
  'svalbard and jan mayen': 'NOR',

  // ── 2. Expansions of Natural Earth's own abbreviations ────────────────────────────
  'bosnia and herzegovina': 'BIH',
  'central african republic': 'CAF',
  'dominican republic': 'DOM',
  'equatorial guinea': 'GNQ',
  'antigua and barbuda': 'ATG',
  'saint kitts and nevis': 'KNA',
  'st kitts and nevis': 'KNA',
  'saint vincent and the grenadines': 'VCT',
  'st vincent and the grenadines': 'VCT',
  'saint vincent grenadines': 'VCT',
  'saint pierre and miquelon': 'SPM',
  'st pierre and miquelon': 'SPM',
  'saint lucia': 'LCA',
  'st lucia': 'LCA',
  'saint barthelemy': 'BLM',
  'st barthelemy': 'BLM',
  'saint martin': 'MAF',
  'st martin': 'MAF',
  'saint helena': 'SHN',
  'st helena': 'SHN',
  'sao tome and principe': 'STP',
  'cayman islands': 'CYM',
  'solomon islands': 'SLB',
  'marshall islands': 'MHL',
  'falkland islands': 'FLK',
  'falkland islands malvinas': 'FLK',
  'cook islands': 'COK',
  'faroe islands': 'FRO',
  'faeroe islands': 'FRO',
  'turks and caicos islands': 'TCA',
  'british virgin islands': 'VGB',
  'virgin islands british': 'VGB',
  'us virgin islands': 'VIR',
  'united states virgin islands': 'VIR',
  'virgin islands us': 'VIR',
  'northern mariana islands': 'MNP',
  'pitcairn islands': 'PCN',
  'pitcairn': 'PCN',
  'norfolk island': 'NFK',
  'french polynesia': 'PYF',
  'wallis and futuna': 'WLF',
  'british indian ocean territory': 'IOT',
  'french southern territories': 'ATF',
  'french southern and antarctic lands': 'ATF',
  'south georgia and the south sandwich islands': 'SGS',
  'heard island and mcdonald islands': 'HMD',
  'aland': 'ALA',
  'aland islands': 'ALA',
  'federated states of micronesia': 'FSM',
  'micronesia federated states of': 'FSM',
  'north macedonia': 'MKD',
  'aruba': 'ABW',
  'curacao': 'CUW',

  // ── 3. Codes absent from the table ────────────────────────────────────────────────
  // Kosovo's de-facto ISO-3 is XKX / XK; Natural Earth ships it as KOS.
  'xkx': 'KOS',
  'xk': 'KOS',
  // `UK` is a RESERVED ISO-3166-1 alpha-2 (it is not the assigned code — that is `GB`)
  // and is written far more often than `GB` in hand-entered data.
  // NB: `'uk': 'GBR'` also appears in group 1 above as a colloquialism; one entry serves
  // both readings, so it is not repeated here.
};

/**
 * Case, accents, punctuation and spacing folded away.
 *
 * NFD + a combining-marks strip is what makes `"Côte d'Ivoire"`, `"Cote d’Ivoire"` and
 * `"COTE D IVOIRE"` a single key — without it every accented country would need one alias
 * per typing of it. `'` and `’` both vanish with the punctuation strip, which matters
 * because a browser and a spreadsheet disagree about which apostrophe they emit.
 *
 * Deliberately does NOT strip the word "the", collapse plurals, or do anything else
 * lossy — see the header on why fuzziness is forbidden here.
 */
const normalize = (raw: string): string =>
  raw
    .normalize('NFD')
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Every accepted spelling → ISO-3. Built once at module load. */
const INDEX: Record<string, string> = (() => {
  const index: Record<string, string> = {};
  for (const c of COUNTRY_TABLE) {
    index[normalize(c.a3)] = c.a3;
    if (c.a2) index[normalize(c.a2)] = c.a3;
    index[normalize(c.name)] = c.a3;
  }
  // Aliases last so a curated variant can override a coincidental collision — e.g. the
  // ISO-2 `'us'` would otherwise be ambiguous against nothing, but `'no'` (Norway) and
  // `'in'` (India) are real two-letter codes that also read as English words, and the
  // alias layer is where a deliberate decision about one would go.
  for (const [alias, a3] of Object.entries(COUNTRY_ALIASES)) {
    index[normalize(alias)] = a3;
  }
  return index;
})();

/**
 * A raw `scans.geo_country` value → ISO-3, or **`null` if it does not resolve**.
 *
 * `null` is a real answer and callers must surface it (a count, a list of the offending
 * values) rather than filtering it away. See the header: a dropped country renders as a
 * country with no scans, and nothing on screen distinguishes the two.
 */
export const resolveCountryCode = (
  raw: string | null | undefined,
): string | null => {
  if (raw === null || raw === undefined) return null;
  const key = normalize(String(raw));
  if (!key) return null;
  return INDEX[key] ?? null;
};

/** Every ISO-3 the resolver can emit. The guard checks this equals the asset's key set. */
export const KNOWN_COUNTRY_CODES: readonly string[] = COUNTRY_TABLE.map((c) => c.a3);
